import styled from '@emotion/styled'

interface StatCardProps {
    label: string
    value: number | string
    icon?: React.ReactNode
    color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'yellow'
    trend?: {
        value: number
        isPositive: boolean
    }
}

const colorMap = {
    blue: {
        bg: '#eff6ff',
        text: '#2563eb',
        icon: '#3b82f6',
    },
    green: {
        bg: '#f0fdf4',
        text: '#16a34a',
        icon: '#22c55e',
    },
    purple: {
        bg: '#faf5ff',
        text: '#9333ea',
        icon: '#a855f7',
    },
    orange: {
        bg: '#fff7ed',
        text: '#ea580c',
        icon: '#f97316',
    },
    red: {
        bg: '#fef2f2',
        text: '#dc2626',
        icon: '#ef4444',
    },
    yellow: {
        bg: '#fefce8',
        text: '#ca8a04',
        icon: '#eab308',
    },
}

const StatCardContainer = styled.div<{ color: keyof typeof colorMap }>`
    background: ${({ color }) => colorMap[color].bg};
    border-radius: 8px;
    padding: 16px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
`

const StatHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`

const StatLabel = styled.p<{ color: keyof typeof colorMap }>`
    font-size: 1.2rem;
    color: #6b7280;
    margin: 0;
    font-weight: 500;
`

const StatIcon = styled.div<{ color: keyof typeof colorMap }>`
    font-size: 1.6rem;
    color: ${({ color }) => colorMap[color].icon};
`

const StatValue = styled.p<{ color: keyof typeof colorMap }>`
    font-size: 2.4rem;
    font-weight: 700;
    color: ${({ color }) => colorMap[color].text};
    margin: 0;
    line-height: 1;
`

const StatTrend = styled.div<{ isPositive: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 1.2rem;
    margin-top: 8px;
    color: ${({ isPositive }) => (isPositive ? '#16a34a' : '#dc2626')};
    font-weight: 500;
`

const StatCard = ({ label, value, icon, color = 'blue', trend }: StatCardProps) => {
    return (
        <StatCardContainer color={color}>
            <StatHeader>
                <StatLabel color={color}>{label}</StatLabel>
                {icon && <StatIcon color={color}>{icon}</StatIcon>}
            </StatHeader>
            <StatValue color={color}>{value}</StatValue>
            {trend && (
                <StatTrend isPositive={trend.isPositive}>
                    {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
                </StatTrend>
            )}
        </StatCardContainer>
    )
}

export default StatCard
